import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useNavigate, useSearchParams } from 'react-router'
import invariant from 'tiny-invariant'

import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import { useIsRoute } from '@/hooks/useIsRoute'
import useNotyf from '@/hooks/useNotyf'
import {
  API_PATH_INTERNAL,
  DATES,
  REFERRAL_IMPORT_DIRECTION,
  REFERRAL_SOURCE,
  REFERRALS_TAB,
} from '@/lib/constants'
import { normalizeDateOfBirth } from '@/lib/helpers/dateHelpers'
import { isNotProduction } from '@/lib/helpers/envHelpers'
import { getErrorDebugInfo } from '@/lib/helpers/helperFunctions'
import { buildCreateHistoricalReferralRequest } from '@/lib/helpers/referralImportHelpers'
import queryClient from '@/lib/queryClient'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { ROUTES } from '@/lib/routes'
import { SEARCH_PARAMS } from '@/lib/searchParams'
import clientService from '@/lib/services/clientService'
import constraintViolationService from '@/lib/services/constraintViolationService'
import errorTypeService from '@/lib/services/errorTypeService'
import interceptorLogService from '@/lib/services/interceptorLogService'
import referralService from '@/lib/services/referralService'
import serviceService from '@/lib/services/serviceService'
import {
  LogHistoricalReferralData,
  LogHistoricalReferralState,
  resetLogReferralState,
  selectIsLoggingSentReferral,
  selectLogHistoricalReferralState,
  setErrors,
  updateLogData,
} from '@/redux/slices/logHistoricalReferral'
import { CompanyEntity, UserEntity } from '@/types/entities'
import { FormErrors } from '@/types/misc'
import { LogDirectLeadReferralRequest } from '@/types/requests/referrals'

export function useLogHistoricalReferralMutation() {
  const dispatch = useAppDispatch()
  const notyf = useNotyf()
  const isLoggingSentReferral = useAppSelector(selectIsLoggingSentReferral)
  const logHistoricalReferralState = useAppSelector(
    selectLogHistoricalReferralState,
  )
  const navigate = useNavigate()
  const isRoute = useIsRoute()
  const [searchParams] = useSearchParams()

  return useMutation({
    // retry mutation with linear backoff delay (for 4 retries the last delay is 3000 ms, total time 7.5 s)
    retry: import.meta.env.VITE_REFERRAL_MAX_RETRIES,
    retryDelay: (attempt) => attempt * 750,
    mutationFn: async ({
      logData,
      userCompany,
      userCompanyUser,
    }: {
      logData: LogHistoricalReferralData
      userCompany: Pick<CompanyEntity, 'id'>
      userCompanyUser: Pick<UserEntity, 'id'>
    }) => {
      // Handle service
      if (logData.newServiceAreaId) {
        // Create a new service
        try {
          await serviceService.createService({
            company: `${API_PATH_INTERNAL}/companies/${userCompany.id}`,
            serviceArea: `${API_PATH_INTERNAL}/service-areas/${logData.newServiceAreaId}`,
          })
        } catch (error) {
          /**
           * Ignore 422 errors from this endpoint as we can get them if the
           * service already exists.
           * @todo: Use a more specific error type check instead of just checking
           * for the HTTP status code.
           */
          if (!errorTypeService.isUnprocessibleEntityError(error)) {
            throw error
          }
        }
      }

      // Handle client
      let existingClient = logData.existingClient

      if (!logData.existingClient) {
        const dateOfBirth = logData.newClientDateOfBirth
          ? normalizeDateOfBirth(logData.newClientDateOfBirth)
          : undefined

        // create a new user's client if missing
        existingClient =
          await clientService.createClientForReferralOrFindExisting(
            userCompany.id,
            {
              firstName: logData.newClientFirstName,
              lastName: logData.newClientLastName,
              email: logData.newClientEmail,
              telephone: logData.newClientTelephone || undefined,
              dateOfBirth,
              clientId: '',
            },
          )

        // update log data state
        dispatch(
          updateLogData({
            existingClient: existingClient,
            newClientDateOfBirth: '',
            newClientEmail: '',
            newClientEmailValid: false,
            newClientFirstName: '',
            newClientLastName: '',
          }),
        )
      }

      const source = logData.referralSource
      const directLeadSource = logData.directLeadSource.trim()

      invariant(source, 'Expected logData.referralSource to be set')

      const isIntroducerReferral =
        logData.referralSource === REFERRAL_SOURCE.INTRODUCER

      const isSendingInvite = isIntroducerReferral && !logData.otherCompany

      if (logData.referralSource === REFERRAL_SOURCE.DIRECT) {
        return logDirectLeadReferral(logHistoricalReferralState)
      }

      // Log referral
      const request =
        logData.referralDirection === REFERRAL_IMPORT_DIRECTION.FROM
          ? // Log a received referral
            buildCreateHistoricalReferralRequest({
              importingSource: REFERRAL_IMPORT_DIRECTION.TO,
              importingCompanyClient: existingClient,
              isFromInvite: isSendingInvite,
              isToInvite: false,
              fromCompany: logData.otherCompany,
              fromCompanyUser: logData.otherCompanyUser,
              fromAdviserEmail: logData.advisorEmail,
              toCompany: userCompany,
              toCompanyUser: userCompanyUser,
              toAdviserEmail: '',
              source,
              directLeadSource,

              // - other fields
              circumstances: logData.circumstances,
              hasPausedClientEmails: true,
              inviteFromAdviser: logData.inviteAdvisor,
              inviteToAdviser: logData.inviteAdvisor,
              isLoggedReferral: true,
              multipleFromInvites: false,
              multipleToInvites: false,
              referralDate: logData.dateReferred,
              serviceArea: { id: logData.serviceAreaId },
              status: logData.referralStatus,
            })
          : // Log a sent referral
            buildCreateHistoricalReferralRequest({
              importingSource: REFERRAL_IMPORT_DIRECTION.FROM,
              importingCompanyClient: existingClient,
              isFromInvite: false,
              isToInvite: !logData.otherCompany,
              fromCompany: userCompany,
              fromCompanyUser: userCompanyUser,
              fromAdviserEmail: '',
              toCompany: logData.otherCompany,
              toCompanyUser: logData.otherCompanyUser,
              toAdviserEmail: logData.advisorEmail,
              source,
              directLeadSource,

              // - other fields
              circumstances: logData.circumstances,
              hasPausedClientEmails: true,
              inviteFromAdviser: logData.inviteAdvisor,
              inviteToAdviser: logData.inviteAdvisor,
              isLoggedReferral: true,
              multipleFromInvites: false,
              multipleToInvites: false,
              referralDate: logData.dateReferred,
              serviceArea: { id: logData.serviceAreaId },
              status: logData.referralStatus,
            })

      return referralService.createReferral(request)
    },
    onError: async (error, _data) => {
      let formattedErrors: FormErrors | undefined

      if (errorTypeService.isConstraintViolationListError(error)) {
        formattedErrors = constraintViolationService.formatErrors(error)
      } else {
        if (error instanceof AxiosError) {
          formattedErrors = {
            error: error.response?.data?.detail ?? error.message,
          }
        } else if (error.message) {
          formattedErrors = {
            error: error.message.replace('Invariant failed: ', ''),
          }
        } else {
          formattedErrors = {
            error: 'An error occurred',
          }
        }

        if (isNotProduction()) {
          formattedErrors.debug = JSON.stringify(getErrorDebugInfo(error))
        }
      }

      dispatch(setErrors(formattedErrors))
    },
    onSuccess: async (referral, { logData }) => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.referrals(),
      })

      if (logData.interceptorLogId && referral?.id) {
        try {
          await interceptorLogService.linkReferral(
            logData.interceptorLogId,
            referral.id,
          )
          await queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.interceptorDashboard(),
          })
        } catch {
          notyf.error(
            'The referral was logged, however the interceptor log could not be updated',
          )
        }
      }

      const isViewingDetectedReferrals =
        isRoute(ROUTES._referrals) &&
        searchParams.get(SEARCH_PARAMS.referrals.tab) === REFERRALS_TAB.detected

      if (!isViewingDetectedReferrals) {
        navigate(
          ROUTES.referrals({
            searchParams: {
              tab: isLoggingSentReferral
                ? REFERRALS_TAB.sent
                : REFERRALS_TAB.received,
            },
          }),
        )
      }

      dispatch(resetLogReferralState())
    },
  })
}

function logDirectLeadReferral(
  logHistoricalReferralState: LogHistoricalReferralState,
) {
  const { logData, selectedUser } = logHistoricalReferralState

  const referralMappedStatusId = logData.referralStatus?.id

  invariant(
    referralMappedStatusId,
    'Expected logData.referralStatus.id to be set',
  )

  invariant(selectedUser, 'Expected selectedUser to be set')

  const request: LogDirectLeadReferralRequest = {
    firstName: logData.existingClient?.firstName || logData.newClientFirstName,
    lastName: logData.existingClient?.lastName || logData.newClientLastName,
    email: logData.existingClient?.email || logData.newClientEmail,
    telephone: logData.existingClient?.telephone || logData.newClientTelephone,
    serviceAreaIds: [logData.serviceAreaId],
    circumstances: logData.circumstances,
    referralMappedStatusId: referralMappedStatusId,
    directLeadSource: logData.directLeadSource,
    loggedByUser: selectedUser.user['@id'],
    referralDate: DATES.endOfToday.toISOString(),
    toCompanyUserIds: logData.selectedToCompanyUsers.map(
      (companyUser) => companyUser.companyUserId,
    ),
    shouldNotifyToCompanyUsers: logData.shouldNotifyToCompanyUsers,
  }

  return referralService.logDirectLeadReferral(request)
}
